/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-no-undef */
/* eslint-disable multiline-ternary */
import AutoCompleteInput from './autocomplete_input';
import CheckboxInputComponent from './checkbox_input';
import DateInputComponent from './date_input';
import LabelWrapper from '../../wrapper/label';
import PhoneInputComponent from './phone_input';
import QuilInputComponent from './quill_input';
import styled from 'styled-components';
import TextFieldInput from './text_field';
import TimeInput from './time_input';
import {
    Box,
    InputAdornment,
    SelectChangeEvent,
    TextField
} from '@mui/material';
import React, { ChangeEvent, ReactNode } from 'react';

/**
 * @heading1: Custom Input Component
 * @heading3: Interface
 * @heading4: CustomInput Props (All Optional)
 * @ol:
 * label {string}
 * type {'text'|'checkbox'|'date'|'password'|'email'|string}
 * value {string | number}
 * onChange {(event: React.ChangeEvent<HTMLInputElement>) => void}
 * onChangeForPhone?: (event: string) => void;
 * checked {boolean}
 * placeholder {string}
 * error {boolean | string | undefined}
 * className {string}
 * onBlur {(event: React.FocusEvent<HTMLInputElement>) => void}
 * secondInput {boolean}
 * disabled {boolean}
 * style {React.CSSProperties}
 * min {string}
 * mask {string | null}
 * lazyPlaceholder {boolean}
 * placeholderChar {string}
 * mandatoryFields {boolean}
 * showError {boolean}
 * errorText {string}
 * inputProps {unknown}
 * @separator:
 */
interface QuillEditorRef {
  insertTextAtCursor: (text: string) => void;
}

interface CustomInputProps {
    label?: string | ReactNode;
    type?: 'text' | 'number' | 'checkbox' | 'date' | 'password' | 'email' | 'phone' | 'textarea' | 'time' | 'color' | 'unknown' | 'autocomplete' | 'quill';
    value?: string | number | boolean | Date | unknown[] | undefined;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeForAutoComplete?: (event: unknown[]) => void;
    options?: { id: number; name: string }[];
    onChangeForPhone?: (event: string) => void;
    checked?: boolean;
    placeholder?: string;
    error?: boolean | string | undefined;
    className?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    secondInput?: boolean;
    disabled?: boolean;
    style?: React.CSSProperties;
    min?: string;
    mask?: string | null;
    lazyPlaceholder?: boolean;
    placeholderChar?: string;
    mandatoryFields?: boolean;
    showError?: boolean;
    errorText?: string;
    maxLength?: number;
    quillEditorRef?: React.RefObject<QuillEditorRef>;
    [key: string]: unknown;
}

/**
 * @heading3: Styled Components
 * @heading4: Styled TextField
 * @block_quotes: Adds red border styling for error state.
 * @separator:
 */
export const StyledTextField = styled(TextField)`
.Mui-error {
    border-color: red;
}
`;

/**
 * @heading2: CustomInput Component
 * @block_quotes: A reusable input field component supporting multiple types like text, checkbox, date, and password.
 * @separator:
 * @heading3: Parameter {CustomInputProps} props
 * @block_quotes: Props for configuring the input field.
 * @separator:
 */
const CustomInput: React.FC<CustomInputProps> = ({
    label = '',
    type = 'text',
    value = '',
    onChange = () => null,
    onChangeForPhone = () => null,
    onChangeForAutoComplete = () => null,
    options = [],
    checked,
    placeholder = '',
    error,
    className,
    onBlur,
    secondInput = false,
    disabled = false,
    style,
    min,
    mask = null,
    // lazyPlaceholder = false,
    // placeholderChar = '_',
    mandatoryFields = false,
    showError = false,
    // maxLength = 500,
    errorText = '',
    quillEditorRef,
    ...props
}) => {
    /**
     * @heading3: Variables
     * @ol:
     * Flag to check if adornment is required for Phone Number input.
     * State to manage visibility of password input.
    * @separator:
     */
    const showAdornment = label === 'Phone Number';
    // const [showPassword, setShowPassword] = useState(false);

    /**
     * @heading3: Functions
     * @heading4: Toggles the visibility of the password input field.
     * @code: handleTogglePasswordVisibility();
    * @separator:
     */
    // const handleTogglePasswordVisibility = () => {
    //     setShowPassword((prev) => !prev);
    // };

    const handlePhoneInputChange = (phoneValue: string) => {
        if (onChangeForPhone) {
            onChangeForPhone(phoneValue);
        }
    };

    return (
        <div
            className={className || ''}
            style={{
                width: '100%',
                marginLeft: secondInput ? '20px' : '0',
                ...style
            }}
        >
            {/* Render label wrapper for all input types except checkbox */}
            {label && type !== 'checkbox' && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <LabelWrapper
                        label={label}
                        isMandatory={mandatoryFields}
                        showError={showError}
                        errorText={errorText}
                    />
                    {/* {type === 'textarea' && (
                        <Typography variant="caption" color="textSecondary" align="right">
                            {String(value).length} / 500
                        </Typography>
                    )} */}
                </Box>
            )}
            {/* Checkbox input rendering */}
            {
                type === 'time'
                    ? (
                        <TimeInput
                            label={label as string}
                            value={value as string}
                            onChange={(newValue: SelectChangeEvent) => onChange({ target: { value: newValue.target.value }} as ChangeEvent<HTMLInputElement>)}
                            placeholder={placeholder}
                            disabled={disabled}
                            error={Boolean(error)}
                            onBlur={onBlur as ((event: ChangeEvent<HTMLInputElement>) => void)}
                            {...props}
                        />
                    )
                    : type === 'autocomplete'
                        ? (
                            <AutoCompleteInput
                                label={label}
                                value={value as number[]}
                                onChange={(newValue) => onChangeForAutoComplete(newValue)}
                                options={options}
                                placeholder={placeholder}
                                disabled={disabled}
                                error={Boolean(error)}
                                onBlur={onBlur}
                                {...props}
                            />
                        )
                        : type === 'checkbox'
                            ? (
                                <CheckboxInputComponent
                                    checked={checked as boolean}
                                    onChange={onChange}
                                    label={label}
                                    error={error as boolean}
                                    {...props}
                                />
                            )
                            : type === 'phone' ? (
                                <PhoneInputComponent
                                    value={value as string}
                                    onChange={handlePhoneInputChange}
                                    error={error as boolean}
                                    errorText={errorText}
                                    placeholder={placeholder}
                                />
                            )
                                /* Date input rendering */
                                : type === 'date'
                                    ? (
                                        <DateInputComponent
                                            label={label as string}
                                            placeholder={placeholder}
                                            error={Boolean(error)}
                                            value={value as string}
                                            onChange={onChange as ((event: ChangeEvent<HTMLInputElement>) => void)}
                                            onBlur={onBlur as ((event: ChangeEvent<HTMLInputElement>) => void)}
                                            min={min as string}
                                            {...props}
                                        />
                                    )
                                    /* Masked input rendering */
                                    : type === 'quill'
                                    ? (
                                        <QuilInputComponent
                                            ref={quillEditorRef}
                                            value={value as string}
                                            onChange={handlePhoneInputChange}
                                            placeholder={placeholder}
                                            disabled={disabled}
                                            error={Boolean(error)}
                                            {...props}
                                        />
                                    )
                                    : mask
                                        ? (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                boxSizing="border-box"
                                                style={{ position: 'relative' }}
                                                marginTop="0.8rem"
                                                marginBottom="40px"
                                            >
                                                {showAdornment && (
                                                    <InputAdornment
                                                        position="start"
                                                        style={{ position: 'absolute', left: 8 }}
                                                    >
                                                        🇵🇰
                                                    </InputAdornment>
                                                )}
                                                <Box width="100%" boxSizing="border-box">
                                                    {/*
                                        <MaskedInput
                                        mask={mask}
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        lazy={lazyPlaceholder}
                                        placeholderChar={placeholderChar}
                                        disabled={disabled}
                                        label={label}
                                        error={Boolean(error)}
                                        style={style}
                                        {...props}
                                        />
                                        */}
                                                </Box>
                                            </Box>
                                        )
                                        /* Default input rendering */
                                        : (
                                            <TextFieldInput
                                                label={label}
                                                type={type}
                                                placeholder={placeholder}
                                                error={error as boolean}
                                                value={value}
                                                onChange={onChange}
                                                onBlur={onBlur ?? (() => null)}
                                                disabled={disabled}
                                                {...props}
                                            />
                                        )}
        </div>
    );
};

export default CustomInput;
